import React, { useState } from "react";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { useQuery } from "@tanstack/react-query";
import { useLocale, useTranslations } from "next-intl";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import { AirMeasurement, AirStationMapDetailInfo } from "@/lib/types/air";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/i18n/routing";
import { airStationSlug } from "@/lib/data/air-quality";
import { Skeleton } from "@/components/ui/skeleton";
import { AirQualityComponent } from "@/db/definitions";
import AirComponentChart from "../../_components/AirComponentChart";

type Props = {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  stationId: string;
};

const AirStationDrawer: React.FC<Props> = ({
  isOpen,
  setIsOpen,
  stationId,
}) => {
  const [componentSelected, setComponentSelected] =
    useState<AirQualityComponent | null>(null);
  const t = useTranslations("air-quality");
  const locale = useLocale();
  const {
    data: stationData,
    isLoading: isStationLoading,
    isError: isStationError,
  } = useQuery({
    queryKey: ["resource", "air-quality", "map", "item", stationId],
    staleTime: 1000 * 60 * 30,
    gcTime: 1000 * 60 * 60,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    queryFn: async () => {
      const params = new URLSearchParams({ locale });
      const res = await fetch(
        `/api/site/public/resource/air-quality/station/${stationId}/item?${params.toString()}`,
      );
      if (!res.ok) {
        throw new Error("Network response was not ok");
      }
      const body = (await res.json()) as {
        success: boolean;
        error?: string;
        data?: AirStationMapDetailInfo;
      };

      if (!body.success || !body.data) {
        throw new Error(body.error || "Failed to fetch station data");
      }

      setComponentSelected(body.data.components[0]);

      return body.data;
    },
  });

  const {
    data: measurementsData,
    isLoading: isMeasurementsLoading,
    isError: isMeasurementsError,
  } = useQuery({
    queryKey: [
      "resource",
      "air-quality",
      "map",
      "measurements",
      stationId,
      componentSelected,
    ],
    staleTime: 1000 * 60 * 15,
    gcTime: 1000 * 60 * 60,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    queryFn: async () => {
      if (!componentSelected) return null;
      const params = new URLSearchParams({
        component: componentSelected,
        days: "20",
      });

      const res = await fetch(
        `/api/site/public/resource/air-quality/station/${stationId}/measurements?${params.toString()}`,
      );
      if (!res.ok) {
        throw new Error("Network response was not ok");
      }

      const body = (await res.json()) as {
        success: boolean;
        error?: string;
        data?: AirMeasurement[];
      };

      if (!body.success || !body.data || body.data.length === 0) {
        throw new Error(body.error || "Failed to fetch measurements data");
      }
      return body.data;
    },
    enabled: !!componentSelected,
  });
  return (
    <Drawer open={isOpen} onOpenChange={setIsOpen}>
      <DrawerContent className="flex flex-col gap-4 w-full h-fit lg:h-[400px]">
        <VisuallyHidden.Root>
          <DrawerHeader>
            <DrawerTitle>
              {t("map.drawer.title", {
                id: stationId,
              })}
            </DrawerTitle>
            <DrawerDescription>
              {t("map.drawer.description", {
                id: stationId,
              })}
            </DrawerDescription>
          </DrawerHeader>
        </VisuallyHidden.Root>
        <div className="flex flex-col lg:grid grid-cols-2 gap-5 sm:gap-10 px-5 sm:px-10 pb-10 lg:pb-5 h-full">
          <div className="flex flex-col gap-2 w-full justify-between h-fit lg:h-full relative">
            <DrawerClose asChild className="absolute -top-2 -right-2">
              <Button
                aria-label={t("map.drawer.close")}
                variant="outline"
                size="icon-sm"
              >
                <X className="w-6 h-6" />
              </Button>
            </DrawerClose>
            {stationData ? (
              <>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <h2 className="font-semibold text-lg sm:text-xl">
                      {stationData.locationName} ({stationData.organisation})
                    </h2>
                    <div className="flex flex-row gap-2 items-center flex-wrap">
                      {stationData.municipality && (
                        <Badge variant="outline">
                          {stationData.municipality}
                        </Badge>
                      )}
                      {stationData.components.map((component) => (
                        <Badge
                          variant="secondary"
                          key={component}
                          className="px-2 py-1"
                        >
                          {t(`components.${component}.short-name`)}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    {stationData.description}
                  </p>
                </div>

                <div className="flex flex-row gap-4 flex-wrap items-center pt-2 sm:pt-4">
                  <Button asChild>
                    <Link
                      href={`/resource/air-quality/station/${airStationSlug.getSlug(
                        stationData.id,
                      )}`}
                    >
                      {t("map.drawer.info.button.view-station")}
                    </Link>
                  </Button>

                  {stationData.city && (
                    <Button asChild variant="outline">
                      <Link
                        href={`/resource/air-quality/city/${stationData.city.slug}`}
                      >
                        {t("map.drawer.info.button.view-city", {
                          city: stationData.city.name,
                        })}
                      </Link>
                    </Button>
                  )}
                </div>
              </>
            ) : isStationLoading ? (
              <>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Skeleton className="h-8 w-3/4 rounded-md" />
                    <Skeleton className="h-6 w-1/2 rounded-md" />
                  </div>
                  <Skeleton className="h-24 w-full rounded-md" />
                </div>

                <Skeleton className="h-10 w-1/2 rounded-md" />
              </>
            ) : isStationError ? (
              <div className="flex items-center justify-center italic text-muted-foreground">
                {t("map.drawer.error.info")}
              </div>
            ) : null}
          </div>
          <div className="flex flex-col gap-4 w-full relative">
            {stationData && (
              <div className="flex flex-col gap-4 h-fit lg:h-full">
                <div className="flex flex-row gap-2 flex-wrap">
                  {stationData.components.map((component) => (
                    <Button
                      key={`${stationData.id}-${component}`}
                      variant={
                        componentSelected === component ? "default" : "outline"
                      }
                      onClick={() => setComponentSelected(component)}
                    >
                      {t(`components.${component}.short-name`)}
                    </Button>
                  ))}
                </div>
                {measurementsData ? (
                  <div className="relative h-full">
                    <AirComponentChart
                      component={componentSelected!}
                      data={measurementsData}
                      containerClassName="h-60 lg:h-full"
                    />
                  </div>
                ) : isMeasurementsLoading ? (
                  <div className="w-full justify-around h-full flex flex-col gap-6">
                    <Skeleton className="h-6 w-3/4 rounded-md" />
                    <Skeleton className="min-h-60 lg:min-h-fit lg:h-full sm:mb-6 flex-1 rounded-md" />
                  </div>
                ) : isMeasurementsError ? (
                  <div className="flex items-center justify-center italic text-muted-foreground">
                    {t("map.drawer.error.measurements")}
                  </div>
                ) : null}
              </div>
            )}
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
};

export default AirStationDrawer;
